/**
 * Pre-order property bridge for Rebuy
 * -----------------------------------
 * The pre-order app (Timesact) attaches its line item property by injecting a hidden
 * input into the PDP form. Rebuy adds to cart over AJAX and never submits that form,
 * so items added from a Rebuy widget reach the order without the pre-order notice.
 *
 * This bridge listens for Rebuy's post-add event and patches the line item with the
 * same property the PDP would have produced, reusing Timesact's own label template so
 * the wording can never drift from the PDP.
 *
 * Notes:
 * - Rebuy exposes no pre-add hook, so this necessarily runs AFTER the item is in the cart.
 * - `properties` REPLACES the whole object on change, so Rebuy's own `_source` /
 *   `_widget_id` / `_attribution` must be merged back in or their revenue attribution breaks.
 * - Shopify recomputes the line key when properties change; never cache it.
 *
 * Ticket: asana:1217922966772294
 */

class PreorderRebuyBridge {
  static TA_POLL_MS = 200;
  static TA_MAX_WAIT_MS = 5000;
  static UNRESOLVED_PLACEHOLDER = /\{\{\s*\w+\s*\}\}/;
  static DAYS_PER_PERIOD = { DAY: 1, WEEK: 7, MONTH: 30 };
  static MS_PER_DAY = 86400000;

  #processedKeys = new Set();

  init() {
    document.addEventListener('rebuy:cart.add', (event) => this.#onCartAdd(event));
  }

  async #onCartAdd(event) {
    try {
      const item = event?.detail?.item;
      if (!item || !item.key || this.#processedKeys.has(item.key)) return;
      this.#processedKeys.add(item.key);

      const timesact = await this.#waitForTimesact();
      if (!timesact) return;

      const label = this.#resolveLabel(item, timesact);
      if (!label) return;

      // Already carrying the notice (e.g. added from the PDP) - nothing to do.
      if (item.properties && item.properties[label.key]) return;

      await window.Rebuy.Cart.changeItem({
        id: item.key,
        quantity: item.quantity,
        properties: { ...(item.properties || {}), [label.key]: label.value },
      });
    } catch (error) {
      this.#debug('Failed to attach the pre-order property', error);
    }
  }

  /**
   * Timesact hydrates `window.ta` from localStorage and its API. The Rebuy add event can
   * fire before that finishes, so give it a bounded window rather than dropping the item.
   */
  #waitForTimesact() {
    return new Promise((resolve) => {
      const deadline = Date.now() + PreorderRebuyBridge.TA_MAX_WAIT_MS;
      const check = () => {
        if (window.ta?.products && window.ta?.settings) return resolve(window.ta);
        if (Date.now() >= deadline) return resolve(null);
        setTimeout(check, PreorderRebuyBridge.TA_POLL_MS);
      };
      check();
    });
  }

  #resolveLabel(item, timesact) {
    const product = this.#findProduct(item, timesact);
    if (!product) return null;

    // Back-in-stock and coming-soon campaigns show a different notice - not ours to patch.
    if (product.isBIS || product.isCS) return null;

    const variant = product.variants?.[String(item.variant_id)];
    const config = variant?.config;
    if (!config) return null;

    // Pre-order is decided PER VARIANT. The product-level `isPreorder` flag is only true when
    // EVERY variant is on pre-order, so a partially-preordered product reports false while some
    // of its variants genuinely are on pre-order - the PDP shows the notice for those and we must
    // too. Trusting the product flag silently skipped 4 products on this store.
    if (config.status !== 'ACTIVE') return null;

    const template = timesact.settings?.[config.settingsTemplate];
    const labelText = template?.cart?.labelText;
    if (!labelText?.key || !labelText?.value) return null;

    const value = this.#populate(labelText.value, config, item, template);
    if (!value) return null;

    return { key: labelText.key, value };
  }

  /**
   * `window.ta.products` is keyed by product id, but the Rebuy payload does not always
   * carry one - fall back to locating the product that owns this variant.
   */
  #findProduct(item, timesact) {
    const byId = timesact.products?.[String(item.product_id)];
    if (byId) return byId;

    const variantId = String(item.variant_id);
    return Object.values(timesact.products || {}).find((product) => product?.variants?.[variantId]) || null;
  }

  /**
   * Mirrors Timesact's own placeholder substitution. If any placeholder is left
   * unresolved we return null rather than writing a half-rendered string onto the order.
   */
  #populate(rawValue, config, item, template) {
    const locale = template?.message?.locale || 'en-US';
    const shipDate = this.#shippingDate(config.shippingDate);
    if (!shipDate) return null;

    const daysLeft = Math.max(0, Math.ceil((shipDate.getTime() - Date.now()) / PreorderRebuyBridge.MS_PER_DAY));

    const value = rawValue
      .replace('{{shippingDate}}', shipDate.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }))
      .replace('{{daysLeftUntilShippingDate}}', String(daysLeft))
      .replace('{{preorderQuantity}}', String(item.quantity ?? 1));

    if (PreorderRebuyBridge.UNRESOLVED_PLACEHOLDER.test(value)) {
      this.#debug('Label still holds an unresolved placeholder, skipping', value);
      return null;
    }
    return value;
  }

  #shippingDate(shippingDate) {
    if (!shippingDate || shippingDate.type === 'NO_SET') return null;

    if (shippingDate.type === 'DATE') {
      return shippingDate.date ? new Date(shippingDate.date * 1000) : null;
    }

    if (shippingDate.type === 'PERIOD') {
      const multiplier = PreorderRebuyBridge.DAYS_PER_PERIOD[shippingDate.periodType];
      if (!multiplier || !shippingDate.periodValue) return null;
      const days = multiplier * shippingDate.periodValue;
      return new Date(Date.now() + days * PreorderRebuyBridge.MS_PER_DAY);
    }

    return null;
  }

  #debug(message, detail) {
    if (!window.preorderRebuyBridgeDebug) return;
    console.warn(`[preorder-rebuy-bridge] ${message}`, detail);
  }
}

new PreorderRebuyBridge().init();
